import { useState, useEffect, useCallback } from 'react';
import { Task, findTaskByPrompt } from '@/app/task-bank/public-actions';
import { toast } from 'sonner';
import { TOAST_DURATION_MS } from '@/config/writing-editor-config';

interface UseTaskBankProps {
  initialPrompt?: string;
  onTaskSelected: (task: Task) => void; // Callback when a task is selected
  onPromptManuallyChanged: () => void; // Callback when prompt changes & is different from selected task
}

export function useTaskBank({
  initialPrompt = '',
  onTaskSelected,
  onPromptManuallyChanged,
}: UseTaskBankProps) {
  const [taskBankOpen, setTaskBankOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showIdeas, setShowIdeas] = useState(false);

  // Effect to find matching task when initial prompt changes
  useEffect(() => {
    setSelectedTask(null); // Reset task when initial prompt changes
    if (initialPrompt && initialPrompt.trim()) {
      findTaskByPrompt(initialPrompt)
        .then((task) => {
          if (task) {
            setSelectedTask(task);
            // Optionally call onTaskSelected here if needed upon initial load
          }
        })
        .catch((err) => {
          console.error(
            '[useTaskBank] Error checking for matching task prompt:',
            err
          );
        });
    }
  }, [initialPrompt]);

  // Handler when a task is selected from the modal
  const handleSelectTask = useCallback(
    (task: Task) => {
      setSelectedTask(task);
      onTaskSelected(task); // Notify parent (e.g., useEssayEditorState) to update its prompt
      setTaskBankOpen(false); // Close modal after selection
      setShowIdeas(false); // Reset ideas visibility
    },
    [onTaskSelected]
  );

  // Handler to check prompt changes against the selected task
  const checkPromptChange = useCallback(
    (newPromptValue: string) => {
      if (selectedTask && newPromptValue !== selectedTask.prompt) {
        toast.info('Custom prompt entered', {
          id: 'custom-prompt-info',
          description: 'Task Bank ideas are no longer linked.',
          duration: TOAST_DURATION_MS,
        });
        setSelectedTask(null);
        setShowIdeas(false); // Hide ideas if prompt changes
        onPromptManuallyChanged(); // Notify parent
      }
    },
    [selectedTask, onPromptManuallyChanged]
  );

  return {
    taskBankOpen,
    setTaskBankOpen,
    selectedTask,
    // setSelectedTask, // Expose only if needed externally, prefer handlers
    showIdeas,
    setShowIdeas,
    handleSelectTask,
    checkPromptChange,
  };
}
